/**
 * 미디어 화면의 조건 → 두 경계 타입. 광고 `ads.list` · 오가닉 `feed.page`(검색 화면과 같은 질문).
 *
 * 탭과 정렬이 **어느 원장을 물을지**를 정한다(`toMediaLanes`). 검색어가 비어도 묻는다 — 최신 소재부터 보여 준다.
 *
 * ⚠️ `'use client'` 를 붙이지 않는다 — 순수 변환이다.
 * ⚠️ 광고 목록은 기간을 받지 않는다. 기간 칩은 오가닉에만 걸리고 화면이 그렇게 적는다.
 */

import type { MomentSortType } from 'src/types/moments/moment-ops';
import type { SearchMomentScope } from 'src/types/moments/moment-scope';
import type { AdSort, AdListScope } from 'src/types/ads-meta/ads-meta-scope';
import type { ProductRankingSearchScope } from 'src/types/product-rankings/product-rankings-scope';

import { toLocalPostDateInt } from 'src/utils/moments/moment-scope-values';

import { MEDIA_PAGE_SIZE, MEDIA_AD_CHANNELS, MEDIA_LITE_CHANNELS, MEDIA_ORGANIC_CHANNELS } from '../constants/search-all-media';

import type { MediaTab, MediaSort, MediaFormat, ProductSort, MediaChannel, MediaCountry } from '../constants/search-all-media';

/** 미디어 화면이 쥔 조건 전부 */
export type MediaFilter = {
  /** 확정된 검색어 — 비면 조건 없음 */
  query: string;
  tab: MediaTab;
  /** 오늘까지 거슬러 올라가는 일수 — 오가닉만 */
  days: number;
  /** 비면 전체 채널 */
  channels: MediaChannel[];
  /** 비면(또는 둘 다면) 전체 형식 — 광고·오가닉 둘 다 */
  formats: MediaFormat[];
  /** 비면 전체 국가 — 광고·오가닉 둘 다 */
  countries: MediaCountry[];
  /** 비면 전체 언어(소문자 코드) — 오가닉만 · 고르면 광고를 묻지 않는다 */
  languages: string[];
  sort: MediaSort;
};

export type MediaLanes = { ads: boolean; organic: boolean };

const DAY_MS = 24 * 60 * 60 * 1000;

const AD_ONLY_SORTS: MediaSort[] = ['oldest', 'longest'];
const ORGANIC_ONLY_SORTS: MediaSort[] = ['views', 'likes', 'comments'];

const AD_SORT: Record<MediaSort, AdSort> = {
  recent: 'recent',
  popular: 'duration',
  oldest: 'oldest',
  longest: 'duration',
  views: 'recent',
  likes: 'recent',
  comments: 'recent',
};

const ORGANIC_SORT: Record<MediaSort, MomentSortType> = {
  recent: 'latest',
  popular: 'popular',
  oldest: 'latest',
  longest: 'latest',
  views: 'view',
  likes: 'like',
  comments: 'comment',
};

/**
 * 어느 원장을 묻나 — **탭이 이긴다**. 탭이 한 원장을 가리키면 그 원장은 늘 묻는다.
 *
 * 🔑 정렬·형식은 「전체」 탭에서 두 원장을 섞을 때만 한쪽을 뺀다. 탭으로 원장을 콕 집었는데 정렬이
 * 남아 있다고 그 탭을 비워 버리면, 사용자는 **이유 없이 빈 화면**을 본다(광고 탭에서 「오래 집행순」을
 * 고른 뒤 소셜포스팅 탭으로 옮기면 그렇게 됐다). 안 맞는 정렬은 그 원장의 기본 정렬로 접어 준다
 * (`ORGANIC_SORT`·`AD_SORT`).
 *
 * 채널은 여전히 원장을 가른다 — 광고 채널만 골랐으면 오가닉을, 오가닉 채널만 골랐으면 광고를 묻지 않는다.
 * 언어는 오가닉에만 있어 고르면 광고를 묻지 않는다. 형식은 **두 원장에 다 있다**(오가닉은 `isVideo`).
 */
export function toMediaLanes(filter: Pick<MediaFilter, 'tab' | 'sort' | 'channels' | 'languages'>): MediaLanes {
  // 상품 탭은 광고·오가닉을 묻지 않는다(`toProductSearchScope`)
  if (filter.tab === 'products') return { ads: false, organic: false };
  const adChannel = filter.channels.some((c) => (MEDIA_AD_CHANNELS as readonly string[]).includes(c));
  const organicChannel = filter.channels.some((c) => (MEDIA_ORGANIC_CHANNELS as readonly string[]).includes(c));
  const anyChannel = filter.channels.length === 0;

  if (filter.tab === 'ads') return { ads: anyChannel || adChannel, organic: false };
  if (filter.tab === 'organic') return { ads: false, organic: anyChannel || organicChannel };

  return {
    ads: !ORGANIC_ONLY_SORTS.includes(filter.sort) && (anyChannel || adChannel) && filter.languages.length === 0,
    organic: !AD_ONLY_SORTS.includes(filter.sort) && (anyChannel || organicChannel),
  };
}

/**
 * 상품도 함께 펴는가 — 상품 탭은 늘, 전체 탭은 **상품에 걸 수 없는 조건이 하나도 없을 때만**.
 *
 * 채널·형식·언어·국가·기간은 상품 원장에 없는 축이라, 고른 채로 섞으면 그 조건에 안 맞는 상품이 끼어든다.
 * 광고만·오가닉만 정렬도 마찬가지다.
 */
export function toProductLane(filter: MediaFilter): boolean {
  if (filter.tab === 'products') return true;
  if (filter.tab !== 'all') return false;
  const narrowed = filter.channels.length > 0 || filter.formats.length > 0 || filter.languages.length > 0 || filter.countries.length > 0;
  return !narrowed && !AD_ONLY_SORTS.includes(filter.sort) && !ORGANIC_ONLY_SORTS.includes(filter.sort);
}

/** 둘 다 물을 때는 원장마다 반씩 — 한 번에 보이는 수를 맞춘다 */
export function toMediaLaneSize(lanes: MediaLanes): number {
  return lanes.ads && lanes.organic ? MEDIA_PAGE_SIZE / 2 : MEDIA_PAGE_SIZE;
}

/**
 * 광고 한 페이지. 집행 중인 광고만 보고, 소재를 묶지 않는다(카드 하나 = 소재 하나).
 * @param from 앞 페이지의 `nextFrom`
 */
export function toMediaAdScope(filter: MediaFilter, from: number, limit: number): AdListScope {
  const q = filter.query.trim();
  return {
    ...(q ? { q } : {}),
    sort: AD_SORT[filter.sort],
    groupBy: 'none',
    // 집행 중인 광고만 — 명시해 둔다(빈 `active` 도 서버가 「진행중만」으로 읽지만 그 기본값에 기대지 않는다)
    active: ['active'],
    ...(filter.formats.length > 0 ? { mediaType: [...filter.formats] } : {}),
    ...(filter.countries.length > 0 ? { country: [...filter.countries] } : {}),
    from,
    limit,
  };
}

/**
 * 오가닉 조회 조건 — 검색 화면과 같은 scope 모양. 페이지·정렬은 부르는 쪽이 붙인다.
 * @param now 기준 시각 — 테스트가 고정한다
 */
export function toMediaOrganicScope(
  filter: MediaFilter,
  now: number = Date.now(),
  /**
   * 이 화면이 펼 수 있는 채널 — **소재 찾기는 셋으로 잠겨 있다**(나머지는 Research의 몫).
   * ⚠️ **잠금이 없는 화면(허브)은 반드시 제 목록을 준다** — 안 주면 셋만 펴면서 집계는 전 채널을 세어
   *   「관련 컨텐츠 11,854 · 최근 30일 언급 18,366」처럼 같은 화면의 두 수가 어긋난다(실측).
   */
  allow: readonly MediaChannel[] = MEDIA_LITE_CHANNELS,
  /**
   * **브랜드 태그**로 좁힐 때 — 허브가 준다. 주면 검색어 대신 태그로 찾는다.
   * ⚠️ 소재 찾기는 안 준다 — 거기 검색어는 「그 말이 든 글」을 찾는 것이 맞다.
   */
  brands: readonly string[] = [],
  /**
   * **제품군**으로 좁힐 때 — 소분류의 색인값이다(허브 머리의 제품군).
   * ⚠️ 머리에 적은 조건은 글 목록에도 걸려야 한다 — 집계만 좁히면 「관련 컨텐츠가 왜 더 많지」가 된다.
   */
  categoryTerms: readonly string[] = [],
  /**
   * **해시태그**로 좁힐 때 — 접힌 줄이 품은 표기를 **전부** 받는다.
   * ⚠️ 대표 표기 하나만 주면 다른 대소문자로 쓴 글이 빠져 고르개에 적힌 수와 어긋난다.
   */
  hashtags: readonly string[] = [],
): SearchMomentScope {
  const span = Math.max(1, Math.floor(filter.days));
  // 안 고르면 펼 수 있는 것 다, 골랐으면 그중 고른 것만(펼 수 없는 채널은 걸러 낸다)
  const picked = filter.channels.filter((c) => allow.includes(c));
  const platforms = picked.length > 0 ? picked : [...allow];
  return {
    screen: 'search',
    period: {
      start: toLocalPostDateInt(new Date(now - (span - 1) * DAY_MS)),
      end: toLocalPostDateInt(new Date(now)),
    },
    keyword: filter.query.trim(),
    matchMode: 'expanded',
    /**
     * 🔑 본문뿐 아니라 **계정 아이디·작성자 이름**으로도 찾는다 — 훑는 자리라
     *   「anua 가 올린 글」을 아이디로 치는 사람과 이름으로 치는 사람이 갈린다.
     *   실측(30일 라이트 채널): `anua` 42,466 → 43,288건 · `mindaylee` 1,775 → 1,825건.
     */
    searchFields: ['body', 'account', 'author'],
    platforms,
    ...(brands.length > 0 ? { brands: [...brands] } : {}),
    ...(categoryTerms.length > 0 ? { categoryTerms: [...categoryTerms] } : {}),
    ...(hashtags.length > 0 ? { hashtags: [...hashtags] } : {}),
    ...(filter.countries.length > 0 ? { countries: [...filter.countries] } : {}),
    ...(filter.languages.length > 0 ? { languages: [...filter.languages] } : {}),
    // 둘 다 고른 것은 조건 없음과 같다 — 서버도 같게 읽지만 유선에 싣지 않는다
    ...(filter.formats.length === 1 ? { formats: [...filter.formats] } : {}),
  };
}

/**
 * 상품 탭 한 페이지 — 판매몰 랭킹 검색(`rankings.search`, 날짜 무관). 검색어가 비면 묻지 않는다(부르는 쪽이 막는다).
 * @param from 앞 페이지까지 받은 수
 */
export function toProductSearchScope(query: string, sort: ProductSort, from: number, limit: number): ProductRankingSearchScope {
  return { q: query.trim(), from, limit, ...(sort === 'relevance' ? {} : { sort }) };
}

export function toMediaOrganicSort(sort: MediaSort): MomentSortType {
  return ORGANIC_SORT[sort];
}

/** 다시 조회해야 하는가를 가르는 서명 — 캐시 정체성도 이 문자열이다 */
export function toMediaScopeKey(filter: MediaFilter): string {
  return JSON.stringify({
    q: filter.query.trim(),
    tab: filter.tab,
    days: Math.max(1, Math.floor(filter.days)),
    channels: [...filter.channels].sort(),
    formats: [...filter.formats].sort(),
    countries: [...filter.countries].sort(),
    languages: [...filter.languages].sort(),
    sort: filter.sort,
  });
}
